import React, { useState, useCallback, useEffect } from "react";
import { RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { scrollToTop } from "@/lib/scroll";
import { logger } from "@/lib/logger";
import { syntheticUserService } from "@/lib/api";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { DATA_SOURCES, BIASES, BEHAVIOUR_PROFILES } from "@/data/cardGameData";

import { ConceptStep } from "@/views/cardgame/ConceptStep";
import { IntroStep } from "@/views/cardgame/IntroStep";
import { QRScannerStep } from "@/views/cardgame/QRScannerStep";
import { ComparisonStep } from "@/views/cardgame/ComparisonStep";
import { ReflectionStep } from "@/views/cardgame/ReflectionStep";
import { ConclusionStep } from "@/views/cardgame/ConclusionStep";

const STEPS = [
  { id: "intro", label: { nl: "Intro", en: "Intro" } },
  { id: "concept", label: { nl: "Concept", en: "Concept" } },
  { id: "codeinvoer-round1", label: { nl: "Ronde 1", en: "Round 1" } },
  { id: "codeinvoer-round2", label: { nl: "Ronde 2", en: "Round 2" } },
  { id: "comparison", label: { nl: "Vergelijk", en: "Compare" } },
  { id: "reflection", label: { nl: "Reflecteer", en: "Reflect" } },
  { id: "conclusion", label: { nl: "Afronden", en: "Conclude" } },
];

const emptyRound = () => ({ cards: [], output: "", loading: false });

const copy = {
  nl: {
    workshopTool: "Workshoptool",
    title: "Data Literacy Card Game",
    reset: "Reset",
    resetTitle: "Spel resetten?",
    resetDescription: "Dit wist alle gekozen kaarten, je concept en de outputs.",
    cancel: "Annuleren",
    stepCount: (current, total) => `Stap ${current} van ${total}`,
    generated: "Beide outputs zijn gegenereerd!",
    generationFailed: "Kon de output niet genereren. Probeer het opnieuw.",
    gameReset: "Spel gereset",
  },
  en: {
    workshopTool: "Workshop Tool",
    title: "Data Literacy Card Game",
    reset: "Reset",
    resetTitle: "Reset the game?",
    resetDescription: "This clears all selected cards, your concept, and the outputs.",
    cancel: "Cancel",
    stepCount: (current, total) => `Step ${current} of ${total}`,
    generated: "Both outputs generated!",
    generationFailed: "Could not generate output. Please try again.",
    gameReset: "Game reset",
  },
};

export function CardGameView({ onGoHome, onLanguageChange }) {
  const [step, setStep] = useState("intro");
  const [concept, setConcept] = useState("");
  const [targetGroup, setTargetGroup] = useState("");
  const [language, setLanguage] = useState("nl");
  const [round1, setRound1] = useState(emptyRound());
  const [round2, setRound2] = useState(emptyRound());
  const [comparisonReflections, setComparisonReflections] = useState([]);
  const [reflection, setReflection] = useState("");

  useEffect(() => { scrollToTop({ delay: 30 }); }, [step]);

  const stepIndex = STEPS.findIndex((s) => s.id === step);
  const progress = Math.round(((stepIndex + 1) / STEPS.length) * 100);
  const currentStep = STEPS[stepIndex];
  const t = copy[language] || copy.nl;

  useEffect(() => {
    onLanguageChange?.(language);
  }, [language, onLanguageChange]);

  const goTo = useCallback((id) => setStep(id), []);

  const handleCardScanned = useCallback((round, card) => {
    const setter = round === 1 ? setRound1 : setRound2;
    setter((prev) => {
      if (card._remove) return { ...prev, cards: prev.cards.filter((c) => c.id !== card.id) };
      if (prev.cards.find((c) => c.id === card.id)) return prev;
      return { ...prev, cards: [...prev.cards, card] };
    });
  }, []);

  const generateBoth = useCallback(async () => {
    setRound2((prev) => ({ ...prev, loading: true }));
    const payloadFor = (rd) => ({
      concept,
      targetGroup,
      language,
      selectedDataSources: rd.cards.filter((c) => DATA_SOURCES.find((d) => d.id === c.id)),
      selectedBiases: rd.cards.filter((c) => BIASES.find((b) => b.id === c.id)),
      selectedConfiguration: rd.cards.find((c) => BEHAVIOUR_PROFILES.find((p) => p.id === c.id)) || null,
    });
    try {
      const [d1, d2] = await Promise.all([
        syntheticUserService.generateCardGameOutput(payloadFor(round1)),
        syntheticUserService.generateCardGameOutput(payloadFor(round2)),
      ]);
      setRound1((prev) => ({ ...prev, output: d1.response || "" }));
      setRound2((prev) => ({ ...prev, output: d2.response || "", loading: false }));
      toast.success(t.generated);
      goTo("comparison");
    } catch (err) {
      logger.error("Generation failed", err);
      setRound2((prev) => ({ ...prev, loading: false }));
      toast.error(t.generationFailed);
    }
  }, [round1, round2, concept, targetGroup, language, goTo, t.generated, t.generationFailed]);

  const resetAll = useCallback(() => {
    setConcept("");
    setTargetGroup("");
    setLanguage("nl");
    setRound1(emptyRound());
    setRound2(emptyRound());
    setComparisonReflections([]);
    setReflection("");
    setStep("intro");
    scrollToTop({ delay: 30 });
    toast(t.gameReset);
  }, [t.gameReset]);

  return (
    <div className="pt-8 pb-16">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-center gap-3">
          <img
            src="/brand/dlg-logo.svg"
            alt=""
            aria-hidden="true"
            loading="eager"
            decoding="sync"
            fetchPriority="high"
            className="h-12 w-12 rounded-xl object-contain bg-[rgba(144,92,255,0.10)] ring-1 ring-black/5"
            data-testid="dlg-view-icon"
          />
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#905cff] font-medium">{t.workshopTool}</p>
            <h1 className="font-display text-2xl font-semibold text-black">{t.title}</h1>
          </div>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="sm" className="rounded-xl">
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> {t.reset}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t.resetTitle}</AlertDialogTitle>
              <AlertDialogDescription>{t.resetDescription}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t.cancel}</AlertDialogCancel>
              <AlertDialogAction onClick={resetAll} className="bg-[#905cff] hover:bg-[#5802ff]">{t.reset}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between text-xs text-black/40 mb-1.5">
          <span>{currentStep?.label?.[language] || currentStep?.label?.nl}</span>
          <span>{t.stepCount(stepIndex + 1, STEPS.length)}</span>
        </div>
        <div className="h-1.5 w-full bg-black/8 rounded-full overflow-hidden">
          <div className="h-full bg-[#905cff] transition-[width] duration-300" style={{ width: `${progress}%` }} />
        </div>
        <div className="flex items-center gap-1.5 mt-2">
          {STEPS.map((s, i) => (
            <div key={s.id} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= stepIndex ? "bg-[#905cff]" : "bg-black/10"}`} />
          ))}
        </div>
      </div>

      {step === "intro" && (
        <IntroStep
          language={language}
          setLanguage={setLanguage}
          onNext={() => goTo("concept")}
        />
      )}

      {step === "concept" && (
        <ConceptStep
          concept={concept}
          setConcept={setConcept}
          targetGroup={targetGroup}
          setTargetGroup={setTargetGroup}
          language={language}
          onNext={() => goTo("codeinvoer-round1")}
        />
      )}

      {step === "codeinvoer-round1" && (
      <QRScannerStep
        round={1}
        scannedCards={round1.cards}
        onCardScanned={(card) => handleCardScanned(1, card)}
        onDone={() => goTo("codeinvoer-round2")}
        loading={round1.loading}
        language={language}
      />
    )}

      {step === "codeinvoer-round2" && (
      <QRScannerStep
        round={2}
        scannedCards={round2.cards}
        onCardScanned={(card) => handleCardScanned(2, card)}
        onDone={() => generateBoth()}
        loading={round2.loading}
        language={language}
      />
    )}

      {step === "comparison" && (
      <ComparisonStep
        concept={concept}
        round1={round1}
        round2={round2}
        onNext={() => goTo("reflection")}
        onBack={() => goTo("codeinvoer-round2")}
        language={language}
        comparisonReflections={comparisonReflections}
        setComparisonReflections={setComparisonReflections}
      />
    )}

      {step === "reflection" && (
      <ReflectionStep
        round1={round1}
        round2={round2}
        reflection={reflection}
        setReflection={setReflection}
        comparisonReflections={comparisonReflections}
        onNext={() => goTo("conclusion")}
        onBack={() => goTo("comparison")}
        language={language}
      />
    )}

       {step === "conclusion" && (
      <ConclusionStep
        concept={concept}
        round1={round1}
        round2={round2}
        reflection={reflection}
        comparisonReflections={comparisonReflections}
        onRestart={resetAll}
        onGoHome={onGoHome}
        language={language}
      />
    )}
    </div>
  );
}
